import { lazy, Suspense, type ReactNode } from 'react';
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useLocation,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Banner, Card, Spinner } from './design-system';
import { useAccess } from './hooks/useAccess';
import { RuntimeConfigProvider, useRuntimeConfig } from './providers/RuntimeConfigProvider';
import { passwordLogin } from '@infrastructure/bootstrapMode';
import { SessionProvider, useSessionState } from './providers/SessionProvider';
import { ToastProvider } from './providers/ToastProvider';
import { AreaFocusProvider } from './providers/AreaFocusProvider';
import { IdleLogoutProvider } from './providers/IdleLogoutProvider';
import { readSessionEnded } from './sessionEnd';
import { SensitiveAccessProvider } from './providers/SensitiveAccessProvider';
import {
  InactiveAccountScreen,
  LoginScreen,
  PendingValidationScreen,
  SessionUnreachableScreen,
} from './screens/Login/LoginScreen';
import { HomeScreen } from './screens/Home/HomeScreen';
import { CalendarScreen } from './screens/Calendar/CalendarScreen';
import { ProfileScreen } from './screens/Profile/ProfileScreen';
import { SearchScreen } from './screens/Search/SearchScreen';
import { AbsencesScreen } from './screens/Absences/AbsencesScreen';
import { ApprovalsScreen } from './screens/Approvals/ApprovalsScreen';
import { ReportsScreen } from './screens/Reports/ReportsScreen';
import { AuditScreen } from './screens/Audit/AuditScreen';
import { AdminScreen } from './screens/Admin/AdminScreen';
import { PersonDetailScreen } from './screens/PersonDetail/PersonDetailScreen';
import { ConfigScreen } from './screens/System/SystemScreens';
import { AreaCatalogsScreen } from './screens/AreaCatalogs/AreaCatalogsScreen';
import { AppShell } from './shell/AppShell';
import { PanoramaScreen } from './screens/Panorama/PanoramaScreen';
import { BlogScreen } from './screens/Blog/BlogScreen';
import { canOpen, findDestination } from './shell/navigation';
import { useScopedAreaId } from './providers/AreaFocusProvider';
import { ROLE_LABEL, type Permission } from '@domain/vocabulary';
import { getServices } from '@infrastructure/services';
import { asId } from '@shared/brand';

/**
 * Composition and routing.
 *
 * The provider order is load-bearing. `RuntimeConfigProvider` is outermost because
 * the session's role only means something against a permission matrix, and the
 * matrix is runtime configuration — so the config has to exist before a session can
 * be interpreted.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Firestore listeners already push changes, so aggressive refetching would
      // duplicate reads that are billed per document. A minute of staleness on a
      // one-shot query is invisible to a user and materially cheaper at 500 people.
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // A permission failure is a real answer, not a transient fault: retrying it
        // just burns quota and delays the message.
        const message = error instanceof Error ? error.message : String(error);
        if (/permission|denied/i.test(message)) return false;
        return failureCount < 2;
      },
    },
  },
});

export const App = (): ReactNode => (
  <QueryClientProvider client={queryClient}>
    <RuntimeConfigProvider>
      <SessionProvider>
        <ToastProvider>
          {/* Dentro de la sesión: el foco de área sale del rol de quien mira. */}
          <AreaFocusProvider>
            {/*
              Dentro de la sesión y fuera de la puerta: envuelve al `AuthGate`
              para que el aviso se pinte sobre cualquier pantalla —incluida la
              tarjeta de confirmar datos, que es un modal que no se puede cerrar
              y donde alguien puede quedarse leyendo—, y para que cerrar la
              sesión devuelva la aplicación al acceso sin nada más que hacer.
            */}
            <IdleLogoutProvider>
              <SensitiveAccessProvider>
                <AuthGate />
              </SensitiveAccessProvider>
            </IdleLogoutProvider>
          </AreaFocusProvider>
        </ToastProvider>
      </SessionProvider>
    </RuntimeConfigProvider>
  </QueryClientProvider>
);

/**
 * Decides which of the four possible applications to render.
 *
 * "Signed in but not yet authorised" is a first-class state rather than an error
 * path: it is the normal first experience of every new hire, because a corporate
 * Google account authenticates successfully and still holds no role until an
 * administrator validates it.
 */
const AuthGate = (): ReactNode => {
  const { status, signIn, signInWithPassword, signOut, refresh, signInError, signingIn } =
    useSessionState();
  const config = useRuntimeConfig();

  const domain = config.identity.allowedDomains[0] ?? 'knotion.com';

  switch (status.kind) {
    case 'loading':
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'grid',
            placeItems: 'center',
            background: 'var(--color-app-bg)',
          }}
        >
          <Spinner label="Cargando el portal" />
        </div>
      );

    case 'signed-out':
      return (
        <LoginScreen
          companyName={config.identity.commercialName}
          allowedDomain={domain}
          signingIn={signingIn}
          error={signInError}
          demoMode={getServices().isDemo}
          passwordLogin={passwordLogin()}
          sessionEnded={readSessionEnded()}
          idleMinutes={config.rules.idleMinutes}
          onSignIn={() => {
            void signIn();
          }}
          onSignInWithPassword={(email, password) => {
            void signInWithPassword(email, password);
          }}
        />
      );

    case 'pending-validation':
      return (
        <PendingValidationScreen
          email={status.user.email}
          displayName={status.user.displayName}
          companyName={config.identity.commercialName}
          hrEmail={config.identity.hrEmail}
          checking={false}
          onRefresh={() => {
            void refresh();
          }}
          onSignOut={() => {
            void signOut();
          }}
        />
      );

    case 'unreachable':
      return (
        <SessionUnreachableScreen
          companyName={config.identity.commercialName}
          checking={false}
          onRetry={() => {
            void refresh();
          }}
          onSignOut={() => {
            void signOut();
          }}
        />
      );

    case 'inactive':
      return (
        <InactiveAccountScreen
          companyName={config.identity.commercialName}
          hrEmail={config.identity.hrEmail}
          onSignOut={() => {
            void signOut();
          }}
        />
      );

    case 'ready':
      return <RouterProvider router={router} />;
  }
};

/**
 * Guards a route against the live permission matrix.
 *
 * The same predicate the sidebar filters with, applied to a direct URL — otherwise
 * typing `/configuracion` would render a screen the menu hides. The redirect carries
 * the handoff's exact wording, naming the role so the message explains rather than
 * merely refusing.
 */
const RequireAccess = ({ children }: { children: ReactNode }): ReactNode => {
  const { holds, featureOn } = useAccess();
  const { status } = useSessionState();
  const location = useLocation();

  if (status.kind !== 'ready') return null;

  // `isSuper` matters for the one destination reserved to the role rather than to
  // a permission; without it the URL would open what the sidebar hides.
  const isSuper = status.session.role === 'super';
  const crossArea = isSuper || status.session.role === 'global';

  if (!canOpen(location.pathname, { holds, featureOn, isSuper, crossArea })) {
    const destination = findDestination(location.pathname);
    return (
      <Navigate
        to="/"
        replace
        state={{
          denied: `Tu perfil (${ROLE_LABEL[status.session.role]}) no tiene acceso a ${
            destination?.label ?? 'esa sección'
          }.`,
        }}
      />
    );
  }

  return children;
};

const guarded = (element: ReactNode): ReactNode => <RequireAccess>{element}</RequireAccess>;

/**
 * Guarda por permiso, para las rutas que no están en la tabla de navegación.
 *
 * `canOpen` devuelve `true` para una ruta que no conoce —«an unknown path is not a
 * permission failure; the router's 404 handles it»— y eso es correcto para el 404 y
 * exactamente lo contrario de lo que hace falta aquí: a la ficha de una persona no
 * se llega por el menú, así que no tiene destino declarado y `guarded()` la dejaría
 * abierta a cualquiera con sesión.
 */
const RequirePermission = ({
  permission,
  label,
  children,
}: {
  /** Varios = basta con uno: el organigrama se abre para verlo o para armarlo. */
  permission: Permission | readonly Permission[];
  label: string;
  children: ReactNode;
}): ReactNode => {
  const { holdsAny } = useAccess();
  const { status } = useSessionState();

  if (status.kind !== 'ready') return null;

  if (!holdsAny(typeof permission === 'string' ? [permission] : permission)) {
    return (
      <Navigate
        to="/"
        replace
        state={{
          denied: `Tu perfil (${ROLE_LABEL[status.session.role]}) no tiene acceso a ${label}.`,
        }}
      />
    );
  }

  return children;
};

/*
 * El organigrama se carga aparte: trae el lienzo (React Flow) y su hoja de
 * estilos, que nadie más usa. Así no pesa en el portal de quien nunca lo abre.
 */
const OrgChartScreen = lazy(() =>
  import('./screens/OrgChart/OrgChartScreen').then((module) => ({
    default: module.OrgChartScreen,
  })),
);

const ORG_CHART_PERMISSIONS: readonly Permission[] = ['ver_organigrama', 'editar_organigrama'];

const OrgChartRoute = (): ReactNode => (
  <RequirePermission permission={ORG_CHART_PERMISSIONS} label="el organigrama">
    <Suspense
      fallback={
        <Card>
          <Spinner label="Abriendo el organigrama…" />
        </Card>
      }
    >
      <OrgChartScreen />
    </Suspense>
  </RequirePermission>
);

/** `/organigrama` a secas: el del área que se está mirando —la suya, o la del foco—. */
const OrgChartHome = (): ReactNode => {
  const { status } = useSessionState();
  const areaId = useScopedAreaId(
    status.kind === 'ready' ? status.session.areaId : asId<'AreaId'>(''),
  );
  if (status.kind !== 'ready') return null;
  return <Navigate to={`/organigrama/${areaId}`} replace />;
};

const NotFound = (): ReactNode => (
  <Card>
    <Banner tone="warn">Esa dirección no existe en el portal.</Banner>
  </Card>
);

const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: <HomeScreen /> },
      /* Kn·Blog. Una sola ruta y la pestaña en `?seccion=`: `canOpen` busca la ruta
         exacta, y `/blog/cumpleanos` sería una ruta que no conoce — y la dejaría
         abrir con el módulo apagado. */
      { path: 'blog', element: guarded(<BlogScreen />) },
      { path: 'ausencias', element: guarded(<AbsencesScreen />) },
      { path: 'calendario', element: guarded(<CalendarScreen />) },
      { path: 'perfil', element: guarded(<ProfileScreen />) },
      { path: 'buscar', element: guarded(<SearchScreen />) },

      /* Vacaciones, incapacidades y permisos son pestañas de «Mis ausencias», y cada
         ruta vieja abre la suya: los correos de permisos llevaron a `/permisos` hasta
         el 30 oct 2026, y redirigir sin el tipo los dejaba en Vacaciones. */
      { path: 'solicitudes', element: <Navigate to="/ausencias?tipo=vacaciones" replace /> },
      { path: 'permisos', element: <Navigate to="/ausencias?tipo=permisos" replace /> },
      { path: 'incapacidades', element: <Navigate to="/ausencias?tipo=incapacidades" replace /> },
      { path: 'feriados', element: <Navigate to="/calendario" replace /> },
      { path: 'aprobaciones', element: guarded(<ApprovalsScreen />) },
      { path: 'panorama', element: guarded(<PanoramaScreen />) },
      { path: 'reportes', element: guarded(<ReportsScreen />) },
      { path: 'bitacora', element: guarded(<AuditScreen />) },
      /*
       * El panel administrador, partido en los dos módulos que siempre fue.
       *
       * `/administrador` conserva su URL —hay enlaces y marcadores— y es la mitad
       * de People: el padrón y las altas. `/organizacion` es la de Organization:
       * sub áreas y líderes. Una sola pantalla con el alcance por prop, no dos
       * copias.
       */
      { path: 'administrador', element: guarded(<AdminScreen scope="people" />) },
      { path: 'organizacion', element: guarded(<AdminScreen scope="organization" />) },
      { path: 'configuracion', element: guarded(<ConfigScreen scope="global" />) },

      /*
       * Áreas y Correos dejan de ser pestañas del hub y vuelven a ser pantallas
       * con URL propia — la que ya tenían antes de que el hub las absorbiera.
       *
       * Áreas es Organization y Correos es Communications: el catálogo de qué
       * aviso se manda en cada evento. Mismo alcance que antes, sólo cambia
       * dónde se encuentran. Ver `ConfigScope`.
       */
      { path: 'areas', element: guarded(<ConfigScreen scope="areas" />) },
      { path: 'correos', element: guarded(<ConfigScreen scope="correos" />) },

      /*
       * Los tipos de incapacidad y permiso **del área**. Vive fuera de
       * «Configuración global» a propósito: su destinatario es el administrador
       * de área, no el superusuario. Ver `AreaCatalogsScreen`.
       */
      { path: 'tipos-de-ausencia', element: guarded(<AreaCatalogsScreen />) },

      /*
       * El organigrama de un área. `/organigrama` lleva al del área que se mira;
       * con área, al de ésa — y la pantalla se encarga de que quien no ve todas
       * las áreas sólo abra la suya.
       */
      { path: 'organigrama', element: guarded(<OrgChartHome />) },
      { path: 'organigrama/:areaId', element: <OrgChartRoute /> },

      /*
       * La ficha de una persona. Sin entrada de menú a propósito: se llega pulsando
       * un avatar, y una entrada permanente para algo que siempre habla de alguien
       * concreto no diría a quién.
       */
      {
        path: 'persona/:personId',
        element: (
          <RequirePermission permission="ver_detalle" label="la ficha de una persona">
            <PersonDetailScreen />
          </RequirePermission>
        ),
      },

      /*
       * The old one-screen-per-section URLs. They are tabs now, so these keep
       * any bookmark or emailed link working instead of 404-ing.
       */
      { path: 'features', element: <Navigate to="/configuracion" replace /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
